// Pure formatting and label helpers for the turnover (pre-moveout inspection) feature.
// No React, no styles: shared by the public pages and the vendor's Turnover Jobs page.

export const ASSIGNMENT_STATUS = {
  assigned: { label: "Awaiting quote", tone: "warning" },
  quoted: { label: "Pending approval", tone: "info" },
  approved: { label: "Approved", tone: "success" },
  declined: { label: "Rejected", tone: "danger" },
  cancelled: { label: "Cancelled", tone: "muted" },
};

export const KIND_LABEL = { contractor: "Contractor", cleaner: "Cleaning" };

export const RATING = {
  clean: { label: "Clean", tone: "success" },
  okay: { label: "Okay", tone: "warning" },
  dirty: { label: "Dirty", tone: "danger" },
};

const cad = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" });

/** "$1,250.00" for anything numeric; an em dash for nothing. */
export const money = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? cad.format(n) : "—";
};

const asDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

// Date-only fields (move-out, pre-inspection) are stored as midnight UTC, so they are read
// back in UTC; otherwise a viewer west of Greenwich sees the day before.
export const fmtDate = (v, { utc = false } = {}) => {
  const d = asDate(v);
  if (!d) return "";
  return d.toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...(utc ? { timeZone: "UTC" } : {}),
  });
};

export const fmtDateTime = (v) => {
  const d = asDate(v);
  if (!d) return "";
  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/** "HH:mm" -> "h:mm AM/PM"; anything else passes through. */
export const fmtTime = (hhmm) => {
  if (!hhmm) return "";
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm).trim());
  if (!m) return String(hhmm);
  const h24 = Number(m[1]);
  const suffix = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 || 12;
  return `${h12}:${m[2]} ${suffix}`;
};

export const fmtMoveout = (date, time) => {
  const d = fmtDate(date, { utc: true });
  const t = fmtTime(time);
  if (!d) return t || "Not set";
  return t ? `${d} at ${t}` : d;
};

/** The API sometimes hands back a populated document where a name is expected. */
export const textOf = (v) => {
  if (v && typeof v === "object") return v.title || v.name || "";
  return v === null || v === undefined ? "" : String(v);
};

export const placeLine = (job) =>
  [textOf(job.property), job.building_name, job.unit_number ? `Unit ${job.unit_number}` : ""]
    .filter(Boolean)
    .join(" · ");

export const isVideoKey = (key = "") => /\.(mp4|m4v|mov|webm|avi|mkv)(\?.*)?$/i.test(String(key));

/** Rounds a user-typed amount to cents; null when it is not a positive number. */
export const parseAmount = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
};
